import React, { useEffect, useRef, useState } from 'react';
import CartButton from '../components/CartButton';
import Breadcrumb from '../components/Breadcrumb';
import ExperienceView from './Reserve/ExperienceView';
import GridView from './Reserve/GridView';
import DateTimeView from './Reserve/DateTimeView';
import TicketEntryView from './Reserve/TicketEntryView';
import PackagesView from './Reserve/PackagesView';
import AddonsView from './Reserve/AddonsView';
import CartView from './Reserve/CartView';
import PaymentView from './Reserve/PaymentView';
import ConfirmationView from './Reserve/ConfirmationView';
import { ForceCartOrFallbackError, useCartCache, WithCartCache } from '../context/CartCacheContext';
import {
  ForceEventOrFallbackError,
  useEventCache,
  WithEventCache,
} from '../context/EventCacheContext';
import { readQs, writeQs } from '../../../../../../../shared/lib/querystring';
import CartTimer from '../components/CartTimer';
import { useApp } from '../context/AppContext';
import ReservationEntryView from './Reserve/ReservationEntryView';
import { trackFunnelStep } from '../lib/analytics';

export type RESERVE_STEP =
  | 'grid'
  | 'event'
  | 'date'
  | 'reservationFields'
  | 'ticketFields'
  | 'packages'
  | 'addons'
  | 'cart'
  | 'payment'
  | 'complete';

export interface StepPropsBase<T = void, U = void> {
  onBack: ((specificStep?: RESERVE_STEP) => T) | null;
  onNext: ((specificStep?: RESERVE_STEP, afterNavigate?: () => void) => U) | null;
}

const BASE_STEPS: RESERVE_STEP[] = ['grid', 'event', 'date'];
const END_STEPS: RESERVE_STEP[] = ['cart', 'payment', 'complete'];

function buildSteps(opts: {
  hasReservationFields: boolean;
  hasPackages: boolean;
  hasAddons: boolean;
}): RESERVE_STEP[] {
  const steps: RESERVE_STEP[] = [...BASE_STEPS];

  if (opts.hasReservationFields) steps.push('reservationFields');
  steps.push('ticketFields');
  if (opts.hasPackages) steps.push('packages');
  if (opts.hasAddons) steps.push('addons');

  steps.push(...END_STEPS);
  return steps;
}

interface ReservePropsBase {}

type ReserveProps = ReservePropsBase & React.HTMLProps<HTMLDivElement>;

function Reserve(_props: ReserveProps) {
  const qs = readQs();
  const { eventId, setEventId, event } = useEventCache();
  const { cart, payCart, activeDraft } = useCartCache();
  const { config, dynamicConstants } = useApp();
  const { stickyDiv } = dynamicConstants;

  const [currentStep, setCurrentStep] = useState<RESERVE_STEP>(
    qs.go_success ? 'complete' : eventId ? 'event' : 'grid'
  );
  const trackedStepRef = useRef<RESERVE_STEP | null>(null);
  // this allows us to add filters from the child to the parent when we're on the list view
  const [filtersTarget, setFiltersTarget] = useState<HTMLDivElement | null>(null);
  const [exploreDate, setExploreDate] = useState<Date | undefined>(undefined);

  const hasReservationFields = (event?.eventTemplate?.reservables.fields?.length ?? 0) > 0;
  const hasPackages = (event?.eventTemplate?.reservables.packages?.length ?? 0) > 0;
  const hasAddons = (event?.eventTemplate?.reservables.addons?.length ?? 0) > 0;

  const steps = buildSteps({
    hasReservationFields,
    hasPackages,
    hasAddons,
  });

  const stepToIndex = steps.reduce(
    (acc, step, i) => {
      acc[step] = i;
      return acc;
    },
    {} as Record<RESERVE_STEP, number>
  );

  const indexToStep = steps.reduce(
    (acc, step, i) => {
      acc[i] = step;
      return acc;
    },
    {} as Record<number, RESERVE_STEP>
  );

  const currentStepIndex = stepToIndex[currentStep];

  useEffect(() => {
    if (!steps.includes(currentStep)) {
      setCurrentStep(eventId ? 'event' : 'grid');
    }
  }, [steps, currentStep, eventId]);

  useEffect(() => {
    if (currentStep === 'grid') {
      setEventId(null);
      setExploreDate(undefined);
      const newQs = { ...qs };
      delete newQs.go_success;
      delete newQs.go_event;
      delete newQs.go_cancel;
      writeQs(newQs);
    }
    if (stickyDiv.current) {
      const divToScroll =
        (config.scrollDiv ? document.getElementById(config.scrollDiv) : undefined) || window;

      divToScroll.scrollBy(0, stickyDiv.current.getBoundingClientRect().top - 125);
    }
  }, [currentStep]);

  useEffect(() => {
    const needsEvent = !['grid', 'complete'].includes(currentStep);

    if (needsEvent && !event) return;
    if (trackedStepRef.current === currentStep) return;

    trackedStepRef.current = currentStep;

    trackFunnelStep(currentStep, {
      eventId,
      eventTitle: event?.eventTemplate?.title ?? null,
      cartId: cart?._id ?? (qs.go_success ? String(qs.go_success) : null),
      activeDraft,
    });
  }, [currentStep, event]);

  async function redirectToPayment() {
    const url = await payCart();
    if (!url) return;
    window.location.href = url;
  }

  function goTo(step: RESERVE_STEP) {
    setCurrentStep(step);
  }

  function goNext(specificStep?: RESERVE_STEP, afterNavigate?: () => void) {
    if (specificStep) {
      goTo(specificStep);
      afterNavigate?.();
      return;
    }

    if (currentStepIndex == null) return;
    const next = indexToStep[currentStepIndex + 1];
    if (next) goTo(next);
    afterNavigate?.();
  }

  function goBack(specificStep?: RESERVE_STEP) {
    if (specificStep) {
      goTo(specificStep);
      return;
    }

    if (currentStepIndex == null) return;
    const prev = indexToStep[currentStepIndex - 1];
    if (prev) goTo(prev);
  }

  const allDrafts = cart?.workingDrafts ?? [];

  return (
    <>
      <header className="flex flex-col gap-4 px-4 pb-4 border-b border-border pt-2">
        <div className="flex items-center justify-between gap-6 -mt-1">
          {currentStep === 'grid' ? (
            <div ref={setFiltersTarget} className="flex-1 min-w-0"></div>
          ) : (
            <div className="flex-1 min-w-0 flex items-center">
              <Breadcrumb
                currentStep={currentStepIndex}
                steps={steps}
                onStepClick={stepIndex => {
                  const step = indexToStep[stepIndex];
                  if (step) setCurrentStep(step);
                }}
              />
            </div>
          )}
          {allDrafts.length > 0 && <CartButton onClick={() => setCurrentStep('cart')} />}
        </div>
        <CartTimer />
      </header>

      {currentStep === 'grid' && filtersTarget && (
        <GridView
          onSelectEvent={t => {
            setEventId(t._id);
            setCurrentStep('event');
          }}
          filtersTargetRef={filtersTarget}
        />
      )}

      {currentStep === 'event' && (
        <ForceEventOrFallbackError>
          <ExperienceView
            onNext={goNext}
            onBack={goBack}
            selectedDate={exploreDate}
            onDateChange={setExploreDate}
          />
        </ForceEventOrFallbackError>
      )}

      {currentStep === 'date' && (
        <ForceEventOrFallbackError>
          <DateTimeView initialDate={exploreDate} onNext={goNext} onBack={goBack} />
        </ForceEventOrFallbackError>
      )}

      {currentStep === 'reservationFields' && (
        <ForceEventOrFallbackError>
          <ForceCartOrFallbackError>
            <ReservationEntryView onNext={goNext} onBack={goBack} />
          </ForceCartOrFallbackError>
        </ForceEventOrFallbackError>
      )}

      {currentStep === 'ticketFields' && (
        <ForceEventOrFallbackError>
          <ForceCartOrFallbackError>
            <TicketEntryView onNext={goNext} onBack={goBack} />
          </ForceCartOrFallbackError>
        </ForceEventOrFallbackError>
      )}

      {currentStep === 'packages' && (
        <ForceEventOrFallbackError>
          <ForceCartOrFallbackError>
            <PackagesView onNext={goNext} onBack={goBack} />
          </ForceCartOrFallbackError>
        </ForceEventOrFallbackError>
      )}

      {currentStep === 'addons' && (
        <ForceEventOrFallbackError>
          <ForceCartOrFallbackError>
            <AddonsView onNext={goNext} onBack={goBack} />
          </ForceCartOrFallbackError>
        </ForceEventOrFallbackError>
      )}

      {currentStep === 'cart' && (
        <ForceCartOrFallbackError>
          <CartView onNext={goNext} onBack={() => goBack('grid')} />
        </ForceCartOrFallbackError>
      )}

      {currentStep === 'payment' && (
        <ForceCartOrFallbackError>
          <PaymentView onNext={redirectToPayment} onBack={goBack} />
        </ForceCartOrFallbackError>
      )}

      {currentStep === 'complete' && <ConfirmationView />}
    </>
  );
}

export default function ReserveContainer(props: ReserveProps) {
  return (
    <WithCartCache>
      <WithEventCache>
        <Reserve {...props} />
      </WithEventCache>
    </WithCartCache>
  );
}
